import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const statusBadge = {
  pending_payment: "bg-foreground/10 text-foreground border-foreground/30",
  confirmed: "bg-primary/15 text-primary border-primary/30",
  completed: "bg-primary/10 text-primary border-primary/30",
  cancelled: "bg-muted text-muted-foreground border-border",
};

export const BookingsPanel = () => {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/bookings/for-me").then((r) => setData(r.data));
  }, []);
  useEffect(load, [load]);

  const complete = async (id) => {
    try {
      await api.put(`/bookings/${id}/complete`);
      toast.success("Session marked completed — the seeker can now leave a review.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const { bookings, earnings } = data;

  return (
    <div data-testid="practitioner-bookings-panel">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="earnings-summary">
        {[["Paid sessions", earnings.count], ["Gross (CAD)", `$${earnings.gross}`], ["O.R.E.C 10%", `$${earnings.commission}`], ["Your net", `$${earnings.net}`], ["Paid out", `$${earnings.paid_out}`], ["Balance owing", `$${earnings.balance}`]].map(([label, value]) => (
          <div key={label} className="silk-card rounded-lg p-4">
            <p className="font-display text-2xl text-primary">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{label}</p>
          </div>
        ))}
      </div>
      {bookings.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="practitioner-bookings-empty">No bookings yet. Once you're booked, sessions and earnings appear here.</p>
      ) : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <div key={b.id} className="silk-card rounded-lg p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`practitioner-booking-${b.id}`}>
              <div>
                <p className="text-foreground text-sm">{b.service.name} · {b.date} at {b.time}</p>
                <p className="text-xs text-muted-foreground">{b.seeker_name} · net ${b.net_amount} CAD after 10% commission</p>
              </div>
              <div className="flex items-center gap-2">
                {b.payout_status === "paid" && (
                  <Badge variant="outline" className="font-normal border bg-primary/10 text-primary border-primary/30" data-testid={`payout-chip-${b.id}`}>
                    paid out {new Date(b.payout_at).toLocaleDateString("en-CA")}
                  </Badge>
                )}
                <Badge variant="outline" className={`font-normal border ${statusBadge[b.status] || ""}`}>{b.status.replace("_", " ")}</Badge>
                {b.status === "confirmed" && (
                  <Button size="sm" variant="outline" className="rounded-full" data-testid={`complete-booking-${b.id}`} onClick={() => complete(b.id)}>
                    Mark completed
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
